// src/pages/SopralluoghiSenzaCommessaPage.js
// ═══════════════════════════════════════════════════════════════════════════
// SOPRALLUOGHI SENZA COMMESSA (ottobre 2026, W416)
// ═══════════════════════════════════════════════════════════════════════════
// Il sopralluogo fatto prima che la commessa esista: per un cliente o un potenziale cliente,
// legato se si vuole alla Richiesta Commerciale da cui nasce. Stesso verbale della commessa
// (foto, anomalie, firma, PDF) e stessa numerazione SOP. Qui restano quelli non ancora
// collegati; «Collega a commessa» li porta nella commessa quando nasce (anche dalla scheda
// Sopralluogo della commessa, che li propone da sola per lo stesso cliente).
// Permesso «sopralluoghi-senza-commessa» (utils/sopralluoghi.js).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { useCanSee } from "../hooks/useCanSee";
import { Plus, FileText } from "lucide-react";
import { SopralluogoModal, SopralluogoCard } from "../components/SopralluogoTab";
import CollegaSopralluogo from "../components/CollegaSopralluogo";
import { ElencoBozze, delGiorno } from "../components/AvvisoBozza";
import { prefissoBozze } from "../utils/bozzeLocali";
import { PERMESSO_SENZA_COMMESSA, clienteDelSopralluogo } from "../utils/sopralluoghi";

export default function SopralluoghiSenzaCommessaPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();
  const canSee = useCanSee();
  const puo = !!(isOrgAdmin || isPlatformOwner || canSee(PERMESSO_SENZA_COMMESSA));
  const [elenco, setElenco] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [modulo, setModulo] = useState(null); // { sopralluogo|null, riprendi }
  const [daCollegare, setDaCollegare] = useState(null);
  const [soloMiei, setSoloMiei] = useState(false);
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    if (!orgId) return undefined;
    getDoc(doc(db, "organizations", orgId)).then(s => setOrgInfo(s.exists() ? s.data() : null)).catch(() => {});
    return onSnapshot(query(collection(db, "sopralluoghi"), where("orgId", "==", orgId), where("projectId", "==", "")),
      s => setElenco(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")))),
      () => setElenco([]));
  }, [orgId]);

  const testo = cerca.trim().toLowerCase();
  const visibili = useMemo(() => elenco.filter(s => (!soloMiei || s.creatoDa?.uid === currentUser?.uid)
    && (!testo || [s.numero, clienteDelSopralluogo(s), s.luogo, s.motivoSopralluogo, s.rcNumero].some(v => String(v || "").toLowerCase().includes(testo)))),
  [elenco, soloMiei, testo, currentUser?.uid]);

  const elimina = async (s) => {
    if (!await confirm(`Eliminare il sopralluogo ${s.numero || ""}?`, { type: "danger" })) return;
    try { await deleteDoc(doc(db, "sopralluoghi", s.id)); addToast("Eliminato", "info"); }
    catch { addToast("Non è stato possibile eliminarlo", "error"); }
  };
  const puoEliminare = (s) => isOrgAdmin || isPlatformOwner || s.creatoDa?.uid === currentUser?.uid;

  if (!puo) {
    return (
      <div style={{ padding: 32, maxWidth: 640, fontFamily: "'Inter',sans-serif" }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b" }}>Sopralluoghi senza commessa</h2>
        <p style={{ fontSize: 13, color: "#7c3aed", lineHeight: 1.6 }}>Per fare un sopralluogo prima che esista la commessa serve l'abilitazione «Sopralluoghi senza commessa». Contatta Admin per farti abilitare.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px", maxWidth: 980, fontFamily: "'Inter',sans-serif" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b", margin: 0, display: "flex", alignItems: "center", gap: 8 }}><FileText size={17} color="#2563eb" /> Sopralluoghi senza commessa</h2>
          <p style={{ fontSize: 12.5, color: "#6b7280", margin: "4px 0 0", lineHeight: 1.55 }}>
            Il sopralluogo fatto prima che la commessa esista, per un cliente o un potenziale cliente: stesso verbale, foto, firma e PDF.
            Quando la commessa nasce, «Collega a commessa» lo porta dentro.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setModulo({ sopralluogo: null, riprendi: false })}
          style={{ display: "flex", alignItems: "center", gap: 5, background: "#2563eb", borderColor: "#2563eb" }}>
          <Plus size={13} /> Nuovo sopralluogo
        </button>
      </div>

      {!modulo && <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "sopralluogo", "senza-commessa")} documenti={elenco}
        testoDi={(b, s) => s ? `Modifiche non salvate al sopralluogo ${s.numero || ""}` : `Sopralluogo senza commessa non salvato${delGiorno(b.info?.data)}`}
        onRiprendi={s => setModulo({ sopralluogo: s || null, riprendi: true })} />}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <input aria-label="Cerca" className="input" placeholder="Cerca numero, cliente, luogo, motivo…" value={cerca} onChange={e => setCerca(e.target.value)} style={{ flex: "1 1 220px", maxWidth: 360 }} />
        <label style={{ fontSize: 12.5, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={soloMiei} onChange={e => setSoloMiei(e.target.checked)} /> Solo i miei
        </label>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>{visibili.length} da collegare</span>
      </div>

      {visibili.length === 0 ? (
        <div style={{ textAlign: "center", padding: 50, color: "#9ca3af", fontSize: 13 }}>
          {elenco.length ? "Nessun sopralluogo con questi filtri." : "Nessun sopralluogo in attesa di commessa."}
        </div>
      ) : visibili.map(s => (
        <SopralluogoCard key={s.id} s={s} orgInfo={orgInfo} projectName=""
          onEdit={() => setModulo({ sopralluogo: s, riprendi: false })}
          onDelete={() => (puoEliminare(s) ? elimina(s) : addToast("Lo elimina chi l'ha creato o un amministratore", "info"))}
          onCollega={() => setDaCollegare(s)} />
      ))}

      {modulo && (
        <SopralluogoModal sopralluogo={modulo.sopralluogo} projectId="" projectName="" codiceCommessa="" clienteNome=""
          orgId={orgId} orgInfo={orgInfo} riprendiBozza={modulo.riprendi} commessa={null}
          onClose={() => setModulo(null)} />
      )}
      {daCollegare && (
        <CollegaSopralluogo sopralluogo={daCollegare} orgId={orgId} onClose={() => setDaCollegare(null)}
          onCollegato={p => onNavigate && addToast(`Lo trovi nella commessa ${p.codiceCommessa || p.name}, scheda Sopralluogo`, "info")} />
      )}
    </div>
  );
}
